import React, { useMemo, useState, useRef } from 'react';
import { GripVertical, User } from 'lucide-react';
import type { Player } from '../../types';
import { InjuryBadge } from '../ui/InjuryBadge';
import type { InjuryStatus } from '../../constants/injuries';

export interface BaseballLineupGridStats {
  batting_hand?: 'L' | 'R' | 'S';
  batting_average?: number;
  on_base_percentage?: number;
}

export interface BaseballLineupGridProps {
  /** Lineup slots: array of { order (1-based), player or null } for each slot */
  lineup: Array<{ order: number; player: Player | null }>;
  allPlayers: Player[];
  onPlayerChange: (order: number, playerId: string | null) => void;
  playerStatsMap?: Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>;
  /** Number of batting order slots to show (e.g. 9). Default 9. */
  slotCount?: number;
  /** Optional action to render at the end of each row (e.g. Edit stats button). Called with (order, player). */
  renderRowAction?: (order: number, player: Player | null) => React.ReactNode;
  /** Optional header content (e.g. "Drag players to set the batting order.") */
  headerSubtitle?: React.ReactNode;
}

const formatStat = (value?: number): string => {
  if (value === undefined || value === null) return '---';
  return value.toFixed(3).replace(/^0+/, '');
};

interface PlayerCardProps {
  player: Player;
  stats?: BaseballLineupGridStats;
  isDragging?: boolean;
  onDragStart: (e: React.DragEvent | React.TouchEvent, player: Player) => void;
  onDragEnd: () => void;
  onTouchStart?: (e: React.TouchEvent, player: Player) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  stats,
  isDragging,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd
}) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, player)}
    onDragEnd={onDragEnd}
    onTouchStart={(e) => onTouchStart?.(e, player)}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
    className={`
      flex items-center gap-2 p-2 border rounded cursor-move transition-all touch-none
      ${isDragging
        ? 'opacity-50 border-primary bg-primary/10'
        : 'border-gray-200 hover:border-primary hover:shadow-sm bg-white'
      }
    `}
  >
    <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {player.number != null ? (
        <span className="font-bold text-primary text-sm min-w-[2rem]">#{player.number}</span>
      ) : (
        <User size={16} className="text-gray-400 flex-shrink-0" />
      )}
      <span className="font-medium text-xs truncate w-20 shrink-0 block">
        {player.first_name?.charAt(0) ?? ''}. {player.last_name ?? ''}
      </span>
      {player.injury_status && player.injury_status !== 'H' && (
        <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="xs" />
      )}
    </div>
    <div className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0">
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">Bats</span>
        <span className="font-semibold text-xs text-primary">
          {stats?.batting_hand === 'S' ? 'S' : stats?.batting_hand === 'L' ? 'L' : stats?.batting_hand === 'R' ? 'R' : '---'}
        </span>
      </div>
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">Avg</span>
        <span className="font-semibold text-xs">{formatStat(stats?.batting_average)}</span>
      </div>
      <div className="flex flex-col items-center min-w-[2rem]">
        <span className="text-[9px] text-gray-500 uppercase">OBP</span>
        <span className="font-semibold text-xs">{formatStat(stats?.on_base_percentage)}</span>
      </div>
    </div>
  </div>
);

interface BattingOrderRowProps {
  order: number;
  player: Player | null;
  stats?: BaseballLineupGridStats;
  isDragging: boolean;
  draggedPlayer: Player | null;
  dragOverOrder: number | null;
  onDragOver: (e: React.DragEvent | React.TouchEvent, order: number) => void;
  onDrop: (e: React.DragEvent | React.TouchEvent, order: number) => void;
  onDragStart: (e: React.DragEvent | React.TouchEvent, player: Player) => void;
  onDragEnd: () => void;
  onTouchStart?: (e: React.TouchEvent, player: Player) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
  rowAction?: React.ReactNode;
}

const BattingOrderRow: React.FC<BattingOrderRowProps> = ({
  order,
  player,
  stats,
  isDragging,
  draggedPlayer,
  dragOverOrder,
  onDragOver,
  onDrop,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  rowAction
}) => {
  const isDragOver = draggedPlayer && dragOverOrder === order && draggedPlayer.id !== player?.id;

  return (
    <div
      data-order={order}
      onDragOver={(e) => onDragOver(e, order)}
      onDrop={(e) => onDrop(e, order)}
      onTouchMove={(e) => {
        if (draggedPlayer) {
          const touch = e.touches[0];
          const element = document.elementFromPoint(touch.clientX, touch.clientY);
          let dropZone = element;
          while (dropZone && dropZone.getAttribute('data-order') !== order.toString()) {
            dropZone = dropZone.parentElement;
            if (!dropZone || dropZone === document.body) break;
          }
          if (dropZone?.getAttribute('data-order') === order.toString()) {
            onDragOver(e, order);
          }
        }
      }}
      className={`
        flex items-center gap-3 p-2 transition-all relative
        ${isDragOver ? 'bg-primary/10 border-2 border-dashed border-primary rounded-lg' : ''}
      `}
    >
      {isDragOver && (
        <div className="absolute inset-0 border-2 border-dashed border-primary bg-primary/5 rounded-lg pointer-events-none z-10" />
      )}
      <span className={`text-sm font-semibold min-w-[2rem] z-20 relative ${isDragOver ? 'text-primary' : 'text-gray-700'}`}>
        {order})
      </span>
      {player ? (
        <div className="flex-1 min-w-0 z-20 relative">
          <PlayerCard
            player={player}
            stats={stats}
            isDragging={isDragging}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        </div>
      ) : (
        <div className={`flex-1 border-2 border-dashed rounded p-2 text-center text-xs bg-gray-50 z-20 relative transition-all ${
          isDragOver ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-gray-300 text-gray-400'
        }`}>
          {isDragOver ? 'Drop here' : 'Drop player here'}
        </div>
      )}
      {rowAction != null && <div className="flex-shrink-0 z-20 relative">{rowAction}</div>}
    </div>
  );
};

export const BaseballLineupGrid: React.FC<BaseballLineupGridProps> = ({
  lineup,
  allPlayers,
  onPlayerChange,
  playerStatsMap,
  slotCount = 9,
  renderRowAction,
  headerSubtitle
}) => {
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);
  const [dragOverOrder, setDragOverOrder] = useState<number | null>(null);
  const touchElementRef = useRef<HTMLElement | null>(null);

  const lineupByOrder = useMemo(() => {
    const map = new Map<number, Player | null>();
    lineup.forEach(({ order, player }) => map.set(order, player));
    return map;
  }, [lineup]);

  const orders = useMemo(() => Array.from({ length: slotCount }, (_, i) => i + 1), [slotCount]);

  const playersInLineup = useMemo(() => {
    return new Set(lineup.filter(e => e.player).map(e => e.player!.id));
  }, [lineup]);

  const unassignedPlayers = useMemo(() => {
    return allPlayers.filter(p => !playersInLineup.has(p.id));
  }, [allPlayers, playersInLineup]);

  const getPlayerStats = (playerId: string): BaseballLineupGridStats | undefined => {
    const player = allPlayers.find(p => p.id === playerId);
    if (!player) return undefined;
    if (playerStatsMap) {
      const fromMap = playerStatsMap.get(playerId);
      if (fromMap) {
        return {
          batting_hand: fromMap.batting_hand,
          batting_average: fromMap.batting_average,
          on_base_percentage: fromMap.on_base_percentage
        };
      }
    }
    return {
      batting_hand: (player as Player & { batting_hand?: 'L' | 'R' | 'S' }).batting_hand,
      batting_average: (player as Player & { batting_average?: number }).batting_average,
      on_base_percentage: (player as Player & { on_base_percentage?: number }).on_base_percentage
    };
  };

  const handleDragStart = (e: React.DragEvent | React.TouchEvent, player: Player) => {
    setDraggedPlayer(player);
    if ('dataTransfer' in e) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', '');
    }
  };

  const handleDragEnd = () => {
    setDraggedPlayer(null);
    setDragOverOrder(null);
    touchElementRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent | React.TouchEvent, order: number) => {
    if ('dataTransfer' in e) {
      // Mouse/desktop drag – allow drop
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
    // For touch, we don't prevent default because React/browsers treat
    // touch listeners as passive; we only update visual drag-over state.
    setDragOverOrder(order);
  };

  const handleDrop = (e: React.DragEvent | React.TouchEvent, dropOrder: number) => {
    // Only prevent default for mouse/desktop drag events
    if ('dataTransfer' in e) {
      e.preventDefault();
    }
    setDragOverOrder(null);
    if (!draggedPlayer) return;

    const currentOrder = lineup.find(entry => entry.player?.id === draggedPlayer.id)?.order;
    const existingPlayer = lineupByOrder.get(dropOrder);

    if (existingPlayer && existingPlayer.id !== draggedPlayer.id) {
      if (currentOrder != null) onPlayerChange(currentOrder, existingPlayer.id);
      onPlayerChange(dropOrder, draggedPlayer.id);
    } else if (!existingPlayer) {
      if (currentOrder != null) onPlayerChange(currentOrder, null);
      onPlayerChange(dropOrder, draggedPlayer.id);
    }
    setDraggedPlayer(null);
  };

  const handleTouchStart = (e: React.TouchEvent, player: Player) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    setDraggedPlayer(player);
    touchElementRef.current = e.currentTarget as HTMLElement;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!draggedPlayer) return;
    if (e.cancelable) {
      e.preventDefault();
    }
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    let dropZone = element;
    while (dropZone && dropZone.getAttribute('data-order') == null) {
      dropZone = dropZone.parentElement;
      if (!dropZone || dropZone === document.body) break;
    }
    if (dropZone) {
      const order = parseInt(dropZone.getAttribute('data-order') ?? '0', 10);
      if (order > 0) setDragOverOrder(order);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    if (dragOverOrder != null && draggedPlayer) {
      handleDrop(e, dragOverOrder);
    }
    handleDragEnd();
  };

  return (
    <div className="space-y-4">
      {headerSubtitle != null && (
        <p className="text-xs text-gray-600">{headerSubtitle}</p>
      )}
      <div className="space-y-1">
        {orders.map(order => {
          const player = lineupByOrder.get(order) ?? null;
          const stats = player ? getPlayerStats(player.id) : undefined;
          return (
            <div key={order} data-order={order}>
              <BattingOrderRow
                order={order}
                player={player}
                stats={stats}
                isDragging={draggedPlayer?.id === player?.id}
                draggedPlayer={draggedPlayer}
                dragOverOrder={dragOverOrder}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                rowAction={renderRowAction?.(order, player)}
              />
            </div>
          );
        })}
      </div>
      {unassignedPlayers.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Unassigned Players</h4>
          <div className="space-y-2">
            {unassignedPlayers.map(player => {
              const stats = getPlayerStats(player.id);
              return (
                <PlayerCard
                  key={player.id}
                  player={player}
                  stats={stats}
                  isDragging={draggedPlayer?.id === player.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
